import { Config, ProjectPayload } from "atoms/Rtool/newConfigAtoms";
import type { Powertrain } from "atoms/powertrainAtom";
import { throwIfNotOk } from "./apiError";

// レスポンス契約（issue #34）。型名は BE スキーマ名と一致させてある。
// copy 系（登録先の condition/gear に紐づく copy API）の失敗要素。
export type CopyFailedItem = {
    type?: string | null;
    condition_id?: string | null;
    gear_id?: string | null;
    reason?: string | null;
    detail?: string | null;
};

// copy 系レスポンス（事前検証通過後は常に 200。summary は3分類=succeeded/failed/skipped）。
export type CopyBatchResponse = {
    status: number;
    message?: string;
    summary?: { succeeded: number; failed: number; skipped: number };
    failed_items?: CopyFailedItem[];
    warnings?: unknown[];
};

// R 一括 Excel（datasets/all-data の excel 方式）の warning（null_points 等）。
export type ExcelWarning = {
    type?: string | null;
    condition_id?: string | null;
    code?: string | null;
    detail?: string | null;
};

// R 一括 Excel レスポンス（summary は2分類=succeeded/failed。skipped は無い）。
export type ExcelBatchResponse = {
    status: number;
    message?: string;
    summary?: { succeeded: number; failed: number };
    failed_items?: CopyFailedItem[];
    warnings?: ExcelWarning[];
};

// R 個別 Excel（datasets/items/{dataset_id} の excel 方式）レスポンス。
// 成功は message のみ（failed_items/summary は無い）。内容不正は400、ファイル不存在は404。
export type MessageResponse = {
    status: number;
    message?: string;
};

// 計算セット新規作成
// 計算セット CRUD は calculation 単位（gear を跨ぐ）のため、HEV でも gear セグメントは付かない。
// powertrain セグメントのみ切り替える（既定 "bev"）。
export const submitProject = async (
    authToken: string,
    config: Config,
    payload: ProjectPayload,
    powertrain: Powertrain = "bev",
) => {
    const baseURL = process.env.REACT_APP_URL;

    const url = `${baseURL}/calcs/${powertrain}/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /${encodeURIComponent(config.lot_id)}
    /${encodeURIComponent(config.phase_id)}
    /${encodeURIComponent(config.unit_id)}
    /${encodeURIComponent(config.calculation_id)}`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        },
        body: JSON.stringify(payload),
    });

    await throwIfNotOk(response, "createCalcSet");
    const body = await response.json();
    return body.message;
};

// 作成済み計算セットのdescription編集
export const patchCalculationSetDescription = async (
    authToken: string,
    config: Config,
    description: string,
    powertrain: Powertrain = "bev",
) => {
    const baseURL = process.env.REACT_APP_URL;

    const url = `${baseURL}/calcs/${powertrain}/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /${encodeURIComponent(config.lot_id)}
    /${encodeURIComponent(config.phase_id)}
    /${encodeURIComponent(config.unit_id)}
    /${encodeURIComponent(config.calculation_id)}`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        },
        body: JSON.stringify({ description }),
    });

    await throwIfNotOk(response, "patchCalcSetDesc");
    const body = await response.json();
    return body.message;
};

// 計算セット削除
export const deleteCalculationSet = async (
    authToken: string,
    config: Config,
    powertrain: Powertrain = "bev",
) => {
    const baseURL = process.env.REACT_APP_URL;
    const url = `${baseURL}/calcs/${powertrain}/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /${encodeURIComponent(config.lot_id)}
    /${encodeURIComponent(config.phase_id)}
    /${encodeURIComponent(config.unit_id)}
    /${encodeURIComponent(config.calculation_id)}`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'DELETE',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        },
    });

    await throwIfNotOk(response, "deleteCalcSet");
    const body = await response.json();
    return body.message;
};

// 以下、condition 配下のデータ登録・計算系。
// HEV では calculation の直下に gear が入る（.../<calc>/<gear>/<condition>/... または
// .../<calc>/<gear>/datasets/dimensions）ため、HEV 対応は gear を含む専用関数で別途実装する。
// 既存 BEV のフローは本関数群を継続使用する。

// 他計算セットから全データコピー登録
export const submitAllCopyProject = async (
    authToken: string,
    config: Config,
    fromConfig: Config,
    drive_mode_id: string,
    phenomenon_id: string,
): Promise<CopyBatchResponse> => {
    const baseURL = process.env.REACT_APP_URL;

    const url = `${baseURL}/calcs/bev/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /${encodeURIComponent(config.lot_id)}
    /${encodeURIComponent(config.phase_id)}
    /${encodeURIComponent(config.unit_id)}
    /${encodeURIComponent(config.calculation_id)}
    /${encodeURIComponent(phenomenon_id)}-${encodeURIComponent(drive_mode_id)}
    /datasets/all-data`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        },
        body: JSON.stringify(
            {
                "registration_method": "copy",
                "config": {
                    "project_id": fromConfig.project_id,
                    "destination_id": fromConfig.destination_id,
                    "lot_id": fromConfig.lot_id,
                    "phase_id": fromConfig.phase_id,
                    "unit_id": fromConfig.unit_id,
                    "calculation_id": fromConfig.calculation_id,
                    "condition_id": `${phenomenon_id}-${drive_mode_id}`,
                    // #22: BEV 専用エンドポイント。powertrain を明示して共存時の曖昧性を解消
                    "powertrain": "bev",
                }
            }
        ),
    });

    const body = await response.json().catch(() => ({}));
    const payload = typeof body === "object" && body !== null ? body : {};
    return { status: response.status, ...payload } as CopyBatchResponse;
};

// 丸ごとコピー(#35)・commonコピー(#37)共通の内部実装。両者ともcalc直下・gearセグメント無し・
// body={source:{6識別子,powertrain}}のみで、エンドポイント名のみが異なるため fetch/URL 構築部分を集約する
// （戻り値の組み立てはコピー元関数側に委ね、既存のspread順序を変えない）。
const submitCalcScopedCopy = async (
    endpoint: "copy" | "copy-common",
    authToken: string,
    config: Config,
    fromConfig: Config,
    powertrain: Powertrain,
): Promise<{ status: number; payload: Record<string, unknown> }> => {
    const baseURL = process.env.REACT_APP_URL;

    const url = `${baseURL}/calcs/${powertrain}/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /${encodeURIComponent(config.lot_id)}
    /${encodeURIComponent(config.phase_id)}
    /${encodeURIComponent(config.unit_id)}
    /${encodeURIComponent(config.calculation_id)}
    /datasets/${endpoint}`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        },
        body: JSON.stringify(
            {
                "source": {
                    "project_id": fromConfig.project_id,
                    "destination_id": fromConfig.destination_id,
                    "lot_id": fromConfig.lot_id,
                    "phase_id": fromConfig.phase_id,
                    "unit_id": fromConfig.unit_id,
                    "calculation_id": fromConfig.calculation_id,
                    // BEV/HEV 共存時の曖昧性を解消するため powertrain を明示送信する（#22と同じ理由）
                    "powertrain": powertrain,
                }
            }
        ),
    });

    const body = await response.json().catch(() => ({}));
    const payload = typeof body === "object" && body !== null ? body : {};
    return { status: response.status, payload };
};

// 丸ごとコピー開始の受理応答（issue #43。200→202化に伴いボディが別物になる。BE側の202化と
// 同時デプロイが前提）。409（他コピー実行中）は job_id を含まないため CopyBatchResponse 側で表現する。
export type CopyJobAcceptedResponse = {
    status: 202;
    job_id: string;
    status_url: string;
    total_tasks: number;
    poll_interval_seconds: number;
};

// 他計算セットから計算セット丸ごとコピー登録（issue #35。全条件＋common を無条件上書き）。
// issue #43 により成功応答が 200→202（受理のみ）に変わった。400/404/409/500 はボディ
// {statusCode, message} のみで従来と同じ組み立てのため CopyBatchResponse に収まる。
// payload を先に展開してからstatusで上書きする（202応答bodyの `status` はジョブ状態
// "queued"等であり実HTTPステータスと意味が異なるため、submitCopyCommonと同じ順序で
// 実HTTPステータスを優先する。/code-review xhigh所見#1と同種の注意点）。
export const submitFullCopy = async (
    authToken: string,
    config: Config,
    fromConfig: Config,
    powertrain: Powertrain = "bev",
): Promise<CopyJobAcceptedResponse | CopyBatchResponse> => {
    const { status, payload } = await submitCalcScopedCopy("copy", authToken, config, fromConfig, powertrain);
    return { ...payload, status } as CopyJobAcceptedResponse | CopyBatchResponse;
};

// 他計算セットから common（NTF・諸元）のみコピー登録（issue #37。条件/ギアに紐づかないため
// 丸ごとコピーと同様にURLはcalc直下でgearセグメントが無く、powertrain引数1つで呼び分ける）。
export const submitCopyCommon = async (
    authToken: string,
    config: Config,
    fromConfig: Config,
    powertrain: Powertrain = "bev",
): Promise<CopyBatchResponse> => {
    const { status, payload } = await submitCalcScopedCopy("copy-common", authToken, config, fromConfig, powertrain);
    // payload を先に展開してからstatusを上書きし、BEレスポンスbodyに万一`status`キーが
    // 含まれても実HTTPステータスが優先されるようにする（/code-review xhigh所見#1対応）。
    return { ...payload, status } as CopyBatchResponse;
};

// 他計算セットから個別データコピー登録
export const submitSubCopyProject = async (
    authToken: string,
    config: Config,
    fromConfig: Config,
    drive_mode_id: string,
    phenomenon_id: string,
    dataset_id: string,
): Promise<CopyBatchResponse> => {
    const baseURL = process.env.REACT_APP_URL;

    const url = `${baseURL}/calcs/bev/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /${encodeURIComponent(config.lot_id)}
    /${encodeURIComponent(config.phase_id)}
    /${encodeURIComponent(config.unit_id)}
    /${encodeURIComponent(config.calculation_id)}
    /${encodeURIComponent(phenomenon_id)}-${encodeURIComponent(drive_mode_id)}
    /datasets/items
    /${encodeURIComponent(dataset_id)}`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        },
        body: JSON.stringify(
            {
                "registration_method": "copy",
                "config": {
                    "project_id": fromConfig.project_id,
                    "destination_id": fromConfig.destination_id,
                    "lot_id": fromConfig.lot_id,
                    "phase_id": fromConfig.phase_id,
                    "unit_id": fromConfig.unit_id,
                    "calculation_id": fromConfig.calculation_id,
                    "condition_id": `${phenomenon_id}-${drive_mode_id}`,
                    // #22: BEV 専用エンドポイント。powertrain を明示して共存時の曖昧性を解消
                    "powertrain": "bev",
                }
            }
        ),
    });

    const body = await response.json().catch(() => ({}));
    const payload = typeof body === "object" && body !== null ? body : {};
    return { status: response.status, ...payload } as CopyBatchResponse;
};

// Excelから全データコピー登録
export const submitAllCopyExcel = async (
    authToken: string,
    config: Config,
    drive_mode_id: string,
    phenomenon_id: string
): Promise<ExcelBatchResponse> => {
    const baseURL = process.env.REACT_APP_URL;

    const url = `${baseURL}/calcs/bev/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /${encodeURIComponent(config.lot_id)}
    /${encodeURIComponent(config.phase_id)}
    /${encodeURIComponent(config.unit_id)}
    /${encodeURIComponent(config.calculation_id)}
    /${encodeURIComponent(phenomenon_id)}-${encodeURIComponent(drive_mode_id)}
    /datasets/all-data`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        },
        body: JSON.stringify(
            {
                "registration_method": "excel",
            }
        ),
    });

    const body = await response.json().catch(() => ({}));
    const payload = typeof body === "object" && body !== null ? body : {};
    return { status: response.status, ...payload } as ExcelBatchResponse;
};

// Excelから個別データコピー
export const submitSubCopyExcel = async (
    authToken: string,
    config: Config,
    drive_mode_id: string,
    phenomenon_id: string,
    dataset_id: string,
): Promise<MessageResponse> => {
    const baseURL = process.env.REACT_APP_URL;

    const url = `${baseURL}/calcs/bev/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /${encodeURIComponent(config.lot_id)}
    /${encodeURIComponent(config.phase_id)}
    /${encodeURIComponent(config.unit_id)}
    /${encodeURIComponent(config.calculation_id)}
    /${encodeURIComponent(phenomenon_id)}-${encodeURIComponent(drive_mode_id)}
    /datasets/items
    /${encodeURIComponent(dataset_id)}`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        },
        body: JSON.stringify(
            {
                "registration_method": "excel",
            }
        ),
    });

    const body = await response.json().catch(() => ({}));
    const payload = typeof body === "object" && body !== null ? body : {};
    return { status: response.status, ...payload } as MessageResponse;
};

// 諸元: gear_ratio / motor_order とも {phenomenon_id: float} のフラット形式（BEV/HEV共通）。
// gear は HEV のみ URL から確定し、いずれも drive_mode には依存しない
// （諸元データ仕様変更 #26。BEV も HevDimensions.from_partial_dict と同じ Dict[phenomenon_id] 形式に統一）。
export type DimensionParams = {
    tire_radius: number;
    gear_ratio: Record<string, number>;
    motor_order: Record<string, number>;
};

export type HevDimensionParams = DimensionParams;

// 諸元データ登録(UI入力)
export const submitDimensions = async (
    authToken: string,
    config: Config,
    params: DimensionParams,
) => {
    const baseURL = process.env.REACT_APP_URL;

    const url = `${baseURL}/calcs/bev/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /${encodeURIComponent(config.lot_id)}
    /${encodeURIComponent(config.phase_id)}
    /${encodeURIComponent(config.unit_id)}
    /${encodeURIComponent(config.calculation_id)}
    /datasets/dimensions`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        },
        body: JSON.stringify(
            {
                registration_method: "ui",
                dimensions: {
                    tire_radius: params.tire_radius,
                    gear_ratio: params.gear_ratio,
                    motor_order: params.motor_order,
                },
            }
        ),
    });

    await throwIfNotOk(response, "registerDimensions");

    const body = await response.json();
    return body.message;
};

// R側3計算APIの応答: calc（HEV は gear）単位で一括計算した結果の condition 別4分類サマリ。
// FE は condition_id の列挙のみ表示する（reason/missing_dataset_ids は表示しない、諸元データ仕様変更 #28）。
export type CalculationSummary = {
    succeeded: string[];
    failed: { condition_id: string; reason: string }[];
    missing_data: { condition_id: string; missing_dataset_ids: string[] }[];
    skipped_no_data: string[];
};

// 車室内音レベルの計算（calc 単位・一括呼び出し。URL に condition_id は含まない）
export const submitCabinNoiseCalculation = async (
    authToken: string,
    config: Config,
): Promise<CalculationSummary> => {
    const baseURL = process.env.REACT_APP_URL;

    const url = `${baseURL}/calcs/bev/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /${encodeURIComponent(config.lot_id)}
    /${encodeURIComponent(config.phase_id)}
    /${encodeURIComponent(config.unit_id)}
    /${encodeURIComponent(config.calculation_id)}
    /calculations/cabin-noise`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        },
    });

    await throwIfNotOk(response, "cabinNoiseCalculation");

    return await response.json();
};

// コンポーネント目標値の計算（calc 単位・一括呼び出し。URL に condition_id は含まない）
export const submitComponentTargetCalculation = async (
    authToken: string,
    config: Config,
): Promise<CalculationSummary> => {
    const baseURL = process.env.REACT_APP_URL;

    const url = `${baseURL}/calcs/bev/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /${encodeURIComponent(config.lot_id)}
    /${encodeURIComponent(config.phase_id)}
    /${encodeURIComponent(config.unit_id)}
    /${encodeURIComponent(config.calculation_id)}
    /calculations/component-target`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        },
    });

    await throwIfNotOk(response, "componentTargetCalculation");

    return await response.json();
};

// 周波数変換の計算（calc 単位・一括呼び出し。URL に condition_id は含まない）
export const submitFrequencyConversionCalculation = async (
    authToken: string,
    config: Config,
): Promise<CalculationSummary> => {
    const baseURL = process.env.REACT_APP_URL;

    const url = `${baseURL}/calcs/bev/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /${encodeURIComponent(config.lot_id)}
    /${encodeURIComponent(config.phase_id)}
    /${encodeURIComponent(config.unit_id)}
    /${encodeURIComponent(config.calculation_id)}
    /calculations/frequency-conversion`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        },
    });

    await throwIfNotOk(response, "frequencyConversionCalculation");

    return await response.json();
};

// ============================================================
// HEV 専用（calculation 配下に gear。openapi calcs.yaml の HEV path に対応）
// body 形式は BEV と同じ。gear は URL セグメントで指定する。
// ============================================================

// HEV: 諸元データ登録(UI入力) — URL は .../<calc>/<gear>/datasets/dimensions（condition なし）。
// body の gear_ratio / motor_order は {phenomenon_id: float} フラット形式（HevDimensionParams）。
export const submitDimensionsHev = async (
    authToken: string,
    config: Config,
    gear_id: string,
    params: HevDimensionParams,
) => {
    const baseURL = process.env.REACT_APP_URL;

    const url = `${baseURL}/calcs/hev/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /${encodeURIComponent(config.lot_id)}
    /${encodeURIComponent(config.phase_id)}
    /${encodeURIComponent(config.unit_id)}
    /${encodeURIComponent(config.calculation_id)}
    /${encodeURIComponent(gear_id)}
    /datasets/dimensions`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        },
        body: JSON.stringify(
            {
                registration_method: "ui",
                dimensions: {
                    tire_radius: params.tire_radius,
                    gear_ratio: params.gear_ratio,
                    motor_order: params.motor_order,
                },
            }
        ),
    });

    await throwIfNotOk(response, "registerDimensions");

    const body = await response.json();
    return body.message;
};

// HEV: 車室内音レベルの計算（gear 単位・一括呼び出し。URL に condition_id は含まない）
export const submitCabinNoiseCalculationHev = async (
    authToken: string,
    config: Config,
    gear_id: string,
): Promise<CalculationSummary> => {
    const baseURL = process.env.REACT_APP_URL;

    const url = `${baseURL}/calcs/hev/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /${encodeURIComponent(config.lot_id)}
    /${encodeURIComponent(config.phase_id)}
    /${encodeURIComponent(config.unit_id)}
    /${encodeURIComponent(config.calculation_id)}
    /${encodeURIComponent(gear_id)}
    /calculations/cabin-noise`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        },
    });

    await throwIfNotOk(response, "cabinNoiseCalculation");

    return await response.json();
};

// HEV: コンポーネント目標値の計算（gear 単位・一括呼び出し。URL に condition_id は含まない）
export const submitComponentTargetCalculationHev = async (
    authToken: string,
    config: Config,
    gear_id: string,
): Promise<CalculationSummary> => {
    const baseURL = process.env.REACT_APP_URL;

    const url = `${baseURL}/calcs/hev/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /${encodeURIComponent(config.lot_id)}
    /${encodeURIComponent(config.phase_id)}
    /${encodeURIComponent(config.unit_id)}
    /${encodeURIComponent(config.calculation_id)}
    /${encodeURIComponent(gear_id)}
    /calculations/component-target`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        },
    });

    await throwIfNotOk(response, "componentTargetCalculation");

    return await response.json();
};

// HEV: 周波数変換の計算（gear 単位・一括呼び出し。URL に condition_id は含まない）
export const submitFrequencyConversionCalculationHev = async (
    authToken: string,
    config: Config,
    gear_id: string,
): Promise<CalculationSummary> => {
    const baseURL = process.env.REACT_APP_URL;

    const url = `${baseURL}/calcs/hev/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /${encodeURIComponent(config.lot_id)}
    /${encodeURIComponent(config.phase_id)}
    /${encodeURIComponent(config.unit_id)}
    /${encodeURIComponent(config.calculation_id)}
    /${encodeURIComponent(gear_id)}
    /calculations/frequency-conversion`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        },
    });

    await throwIfNotOk(response, "frequencyConversionCalculation");

    return await response.json();
};

// HEV: Excel から全データコピー登録 — .../<calc>/<gear>/<condition>/datasets/all-data
export const submitAllCopyExcelHev = async (
    authToken: string,
    config: Config,
    gear_id: string,
    drive_mode_id: string,
    phenomenon_id: string,
): Promise<ExcelBatchResponse> => {
    const baseURL = process.env.REACT_APP_URL;

    const url = `${baseURL}/calcs/hev/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /${encodeURIComponent(config.lot_id)}
    /${encodeURIComponent(config.phase_id)}
    /${encodeURIComponent(config.unit_id)}
    /${encodeURIComponent(config.calculation_id)}
    /${encodeURIComponent(gear_id)}
    /${encodeURIComponent(phenomenon_id)}-${encodeURIComponent(drive_mode_id)}
    /datasets/all-data`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        },
        body: JSON.stringify({ "registration_method": "excel" }),
    });

    const body = await response.json().catch(() => ({}));
    const payload = typeof body === "object" && body !== null ? body : {};
    return { status: response.status, ...payload } as ExcelBatchResponse;
};

// HEV: Excel から個別データコピー — .../<calc>/<gear>/<condition>/datasets/items/<dataset_id>
export const submitSubCopyExcelHev = async (
    authToken: string,
    config: Config,
    gear_id: string,
    drive_mode_id: string,
    phenomenon_id: string,
    dataset_id: string,
): Promise<MessageResponse> => {
    const baseURL = process.env.REACT_APP_URL;

    const url = `${baseURL}/calcs/hev/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /${encodeURIComponent(config.lot_id)}
    /${encodeURIComponent(config.phase_id)}
    /${encodeURIComponent(config.unit_id)}
    /${encodeURIComponent(config.calculation_id)}
    /${encodeURIComponent(gear_id)}
    /${encodeURIComponent(phenomenon_id)}-${encodeURIComponent(drive_mode_id)}
    /datasets/items
    /${encodeURIComponent(dataset_id)}`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        },
        body: JSON.stringify({ "registration_method": "excel" }),
    });

    const body = await response.json().catch(() => ({}));
    const payload = typeof body === "object" && body !== null ? body : {};
    return { status: response.status, ...payload } as MessageResponse;
};

// HEV: 他計算セットから全データコピー登録 — .../<calc>/<gear>/<condition>/datasets/all-data
// copy の source config には gear_id / condition_id を含める（calcs.yaml copyHev 例）。
// BEV↔HEV 跨ぎコピーは BE が 400（Cross powertrain copy is not allowed）で弾く＋全体が powertrain モードのため構造的に発生しない。
export const submitAllCopyProjectHev = async (
    authToken: string,
    config: Config,
    fromConfig: Config,
    dest_gear_id: string,
    src_gear_id: string,
    drive_mode_id: string,
    phenomenon_id: string,
): Promise<CopyBatchResponse> => {
    const baseURL = process.env.REACT_APP_URL;
    const condition_id = `${phenomenon_id}-${drive_mode_id}`;

    const url = `${baseURL}/calcs/hev/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /${encodeURIComponent(config.lot_id)}
    /${encodeURIComponent(config.phase_id)}
    /${encodeURIComponent(config.unit_id)}
    /${encodeURIComponent(config.calculation_id)}
    /${encodeURIComponent(dest_gear_id)}
    /${encodeURIComponent(condition_id)}
    /datasets/all-data`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        },
        body: JSON.stringify(
            {
                "registration_method": "copy",
                "config": {
                    "project_id": fromConfig.project_id,
                    "destination_id": fromConfig.destination_id,
                    "lot_id": fromConfig.lot_id,
                    "phase_id": fromConfig.phase_id,
                    "unit_id": fromConfig.unit_id,
                    "calculation_id": fromConfig.calculation_id,
                    "gear_id": src_gear_id,
                    "condition_id": condition_id,
                    // #22: BEV/HEV 共存時の曖昧性を解消するため powertrain を明示送信する
                    "powertrain": "hev",
                }
            }
        ),
    });

    const body = await response.json().catch(() => ({}));
    const payload = typeof body === "object" && body !== null ? body : {};
    return { status: response.status, ...payload } as CopyBatchResponse;
};

// HEV: 他計算セットから個別データコピー登録 — .../<calc>/<dest_gear>/<condition>/datasets/items/<dataset_id>
export const submitSubCopyProjectHev = async (
    authToken: string,
    config: Config,
    fromConfig: Config,
    dest_gear_id: string,
    src_gear_id: string,
    drive_mode_id: string,
    phenomenon_id: string,
    dataset_id: string,
): Promise<CopyBatchResponse> => {
    const baseURL = process.env.REACT_APP_URL;
    const condition_id = `${phenomenon_id}-${drive_mode_id}`;

    const url = `${baseURL}/calcs/hev/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /${encodeURIComponent(config.lot_id)}
    /${encodeURIComponent(config.phase_id)}
    /${encodeURIComponent(config.unit_id)}
    /${encodeURIComponent(config.calculation_id)}
    /${encodeURIComponent(dest_gear_id)}
    /${encodeURIComponent(condition_id)}
    /datasets/items
    /${encodeURIComponent(dataset_id)}`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        },
        body: JSON.stringify(
            {
                "registration_method": "copy",
                "config": {
                    "project_id": fromConfig.project_id,
                    "destination_id": fromConfig.destination_id,
                    "lot_id": fromConfig.lot_id,
                    "phase_id": fromConfig.phase_id,
                    "unit_id": fromConfig.unit_id,
                    "calculation_id": fromConfig.calculation_id,
                    "gear_id": src_gear_id,
                    "condition_id": condition_id,
                    // #22: BEV/HEV 共存時の曖昧性を解消するため powertrain を明示送信する
                    "powertrain": "hev",
                }
            }
        ),
    });

    const body = await response.json().catch(() => ({}));
    const payload = typeof body === "object" && body !== null ? body : {};
    return { status: response.status, ...payload } as CopyBatchResponse;
};

// 1つのコピー元条件から複数の宛先条件へ一括コピー登録（issue #36）。
// 音響性制約（音響↔振動を跨ぐ宛先が含まれる場合の400）はBE側で判定するため、FEは事前フィルタしない。
export const submitCopyConditions = async (
    authToken: string,
    config: Config,
    fromConfig: Config,
    source_condition_id: string,
    destination_condition_ids: string[],
): Promise<CopyBatchResponse> => {
    const baseURL = process.env.REACT_APP_URL;

    const url = `${baseURL}/calcs/bev/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /${encodeURIComponent(config.lot_id)}
    /${encodeURIComponent(config.phase_id)}
    /${encodeURIComponent(config.unit_id)}
    /${encodeURIComponent(config.calculation_id)}
    /datasets/copy-conditions`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        },
        body: JSON.stringify(
            {
                "source": {
                    "project_id": fromConfig.project_id,
                    "destination_id": fromConfig.destination_id,
                    "lot_id": fromConfig.lot_id,
                    "phase_id": fromConfig.phase_id,
                    "unit_id": fromConfig.unit_id,
                    "calculation_id": fromConfig.calculation_id,
                    "condition_id": source_condition_id,
                    "powertrain": "bev",
                },
                "destination_condition_ids": destination_condition_ids,
            }
        ),
    });

    const body = await response.json().catch(() => ({}));
    const payload = typeof body === "object" && body !== null ? body : {};
    return { status: response.status, ...payload } as CopyBatchResponse;
};

// HEV: 1つのコピー元条件から複数の宛先条件へ一括コピー登録（issue #36）。
// URLの{gear_id}は宛先gear、body.source.gear_idはコピー元gear（既存のsubmitAllCopyProjectHevと同じ非対称構造）。
export const submitCopyConditionsHev = async (
    authToken: string,
    config: Config,
    fromConfig: Config,
    dest_gear_id: string,
    src_gear_id: string,
    source_condition_id: string,
    destination_condition_ids: string[],
): Promise<CopyBatchResponse> => {
    const baseURL = process.env.REACT_APP_URL;

    const url = `${baseURL}/calcs/hev/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /${encodeURIComponent(config.lot_id)}
    /${encodeURIComponent(config.phase_id)}
    /${encodeURIComponent(config.unit_id)}
    /${encodeURIComponent(config.calculation_id)}
    /${encodeURIComponent(dest_gear_id)}
    /datasets/copy-conditions`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        },
        body: JSON.stringify(
            {
                "source": {
                    "project_id": fromConfig.project_id,
                    "destination_id": fromConfig.destination_id,
                    "lot_id": fromConfig.lot_id,
                    "phase_id": fromConfig.phase_id,
                    "unit_id": fromConfig.unit_id,
                    "calculation_id": fromConfig.calculation_id,
                    "condition_id": source_condition_id,
                    "gear_id": src_gear_id,
                    "powertrain": "hev",
                },
                "destination_condition_ids": destination_condition_ids,
            }
        ),
    });

    const body = await response.json().catch(() => ({}));
    const payload = typeof body === "object" && body !== null ? body : {};
    return { status: response.status, ...payload } as CopyBatchResponse;
};
